import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './T2AgentPanel.css';

/**
 * OptiPrime T2 — sub-agent panel rendered when the Tax / OptiPrime T2 sub-agent is selected.
 *
 * Tabs:
 *   1. Overview   — filing status, CRA assessment, next deadline countdown
 *   2. Workflow   — phased step-by-step guide with gating against critical docs
 *   3. Package    — readiness summary, build ZIP, email to auditor
 *   4. Conversation — chat with the T2 agent (separate model cascade)
 *
 * Backend: /api/opo/t2/*
 */

function T2StatusBadge({ status }) {
  const map = {
    assessed: { label: 'Assessed', cls: 'filed' },
    submitted: { label: 'Under assessment', cls: 'submitted' },
    'due-soon': { label: 'Due soon', cls: 'due-soon' },
    overdue: { label: 'Overdue', cls: 'overdue' },
    open: { label: 'Open', cls: 'open' },
  };
  const s = map[status] || { label: status, cls: 'open' };
  return <span className={`t2-badge t2-badge--${s.cls}`}>{s.label}</span>;
}

function T2DaysCountdown({ days }) {
  if (days == null) return null;
  const cls = days < 0 ? 'overdue' : days <= 45 ? 'urgent' : days <= 120 ? 'soon' : 'ok';
  const label = days < 0
    ? `${Math.abs(days)} days overdue`
    : days === 0
      ? 'Due today'
      : `${days} days remaining`;
  return <span className={`t2-countdown t2-countdown--${cls}`}>{label}</span>;
}

/**
 * Assessment & Refund card. Lives inside the Overview tab. Persists to
 * t2_period_status via /api/opo/t2/periods/:fye/status. Also handles NOA
 * upload (→ /periods/:fye/noa) and CRA correspondence log
 * (/periods/:fye/correspondence).
 *
 * Design intent: keep every CRA milestone editable from the UI so we never
 * need to hand-edit the `KNOWN_FILINGS` object in the server code again.
 */
function T2AssessmentRefundCard({ period, onReload }) {
  const fye = period.fye;
  const filing = period.filing || {};

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [correspondence, setCorrespondence] = useState([]);
  const [openLog, setOpenLog] = useState(false);
  const [newEntry, setNewEntry] = useState({
    correspondenceType: 'letter',
    occurredAt: new Date().toISOString().slice(0, 10),
    subject: '',
    body: '',
  });
  const [addingEntry, setAddingEntry] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    submittedAt: filing.submittedAt || '',
    noaReceivedAt: filing.noaReceivedAt || '',
    assessedAt: filing.assessedAt || '',
    refundExpectedCad: filing.expectedRefund ?? '',
    refundReceivedCad: filing.refundReceivedCad ?? '',
    refundReceivedAt: filing.refundReceivedAt || '',
    refundDepositAccount: filing.refundDepositAccount || 'CIBC business op',
    refundDepositReference: filing.refundDepositReference || '',
    craStatus: filing.craStatus || '',
    notes: filing.notes || '',
  });

  useEffect(() => {
    // Re-sync form when the parent reloads the summary.
    setForm({
      submittedAt: filing.submittedAt || '',
      noaReceivedAt: filing.noaReceivedAt || '',
      assessedAt: filing.assessedAt || '',
      refundExpectedCad: filing.expectedRefund ?? '',
      refundReceivedCad: filing.refundReceivedCad ?? '',
      refundReceivedAt: filing.refundReceivedAt || '',
      refundDepositAccount: filing.refundDepositAccount || 'CIBC business op',
      refundDepositReference: filing.refundDepositReference || '',
      craStatus: filing.craStatus || '',
      notes: filing.notes || '',
    });
  }, [filing.submittedAt, filing.noaReceivedAt, filing.assessedAt, filing.expectedRefund, filing.refundReceivedCad, filing.refundReceivedAt, filing.refundDepositAccount, filing.refundDepositReference, filing.craStatus, filing.notes]);

  const loadCorrespondence = useCallback(() => {
    fetch(`/api/opo/t2/periods/${encodeURIComponent(fye)}/correspondence`)
      .then((r) => r.json())
      .then((d) => setCorrespondence(d.entries || []))
      .catch(() => setCorrespondence([]));
  }, [fye]);

  useEffect(() => {
    if (openLog) loadCorrespondence();
  }, [openLog, loadCorrespondence]);

  const onFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const persist = () => {
    setSaving(true);
    const payload = {
      submitted_at: form.submittedAt || null,
      noa_received_at: form.noaReceivedAt || null,
      assessed_at: form.assessedAt || null,
      refund_expected_cad: form.refundExpectedCad === '' ? null : Number(form.refundExpectedCad),
      refund_received_cad: form.refundReceivedCad === '' ? null : Number(form.refundReceivedCad),
      refund_received_at: form.refundReceivedAt || null,
      refund_deposit_account: form.refundDepositAccount || null,
      refund_deposit_reference: form.refundDepositReference || null,
      cra_status: form.craStatus || null,
      notes: form.notes || null,
    };
    fetch(`/api/opo/t2/periods/${encodeURIComponent(fye)}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((r) => r.ok ? r.json() : r.json().then((e) => Promise.reject(new Error(e.error || 'Save failed'))))
      .then(() => { onReload && onReload(); })
      .catch((err) => alert(err.message))
      .finally(() => setSaving(false));
  };

  const onNoaFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    if (form.noaReceivedAt) fd.append('noaReceivedAt', form.noaReceivedAt);
    fetch(`/api/opo/t2/periods/${encodeURIComponent(fye)}/noa`, { method: 'POST', body: fd })
      .then((r) => r.ok ? r.json() : r.json().then((err) => Promise.reject(new Error(err.error || 'Upload failed'))))
      .then((d) => {
        setForm((prev) => ({
          ...prev,
          noaReceivedAt: d.status?.noa_received_at || prev.noaReceivedAt,
        }));
        onReload && onReload();
      })
      .catch((err) => alert(err.message))
      .finally(() => {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      });
  };

  const addCorrespondence = () => {
    if (!newEntry.correspondenceType || !newEntry.occurredAt) return;
    setAddingEntry(true);
    fetch(`/api/opo/t2/periods/${encodeURIComponent(fye)}/correspondence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newEntry),
    })
      .then((r) => r.ok ? r.json() : r.json().then((err) => Promise.reject(new Error(err.error || 'Add failed'))))
      .then(() => {
        setNewEntry({
          correspondenceType: 'letter',
          occurredAt: new Date().toISOString().slice(0, 10),
          subject: '',
          body: '',
        });
        loadCorrespondence();
      })
      .catch((err) => alert(err.message))
      .finally(() => setAddingEntry(false));
  };

  const deleteEntry = (id) => {
    if (!window.confirm('Delete this correspondence entry?')) return;
    fetch(`/api/opo/t2/periods/${encodeURIComponent(fye)}/correspondence/${id}`, { method: 'DELETE' })
      .then((r) => r.ok ? r.json() : r.json().then((err) => Promise.reject(new Error(err.error || 'Delete failed'))))
      .then(() => loadCorrespondence())
      .catch((err) => alert(err.message));
  };

  const noaOnFile = period.artifacts?.noa?.present;
  const refundStatusLabel = form.refundReceivedAt
    ? `Refund received ${form.refundReceivedAt} · $${Number(form.refundReceivedCad || 0).toLocaleString()}`
    : form.noaReceivedAt
      ? `NOA received ${form.noaReceivedAt}`
      : form.submittedAt
        ? 'Submitted — awaiting NOA'
        : 'Not yet submitted';

  return (
    <div className="t2-section t2-assessment-card">
      <div className="t2-section-title">
        <span>CRA assessment &amp; refund — FYE {fye}</span>
        <span className="t2-assessment-status">{refundStatusLabel}</span>
      </div>

      <div className="t2-assessment-grid">
        <label>
          Submitted on
          <input type="date" value={form.submittedAt} onChange={onFieldChange('submittedAt')} />
        </label>
        <label>
          NOA received
          <input type="date" value={form.noaReceivedAt} onChange={onFieldChange('noaReceivedAt')} />
        </label>
        <label>
          Assessed on
          <input type="date" value={form.assessedAt} onChange={onFieldChange('assessedAt')} />
        </label>
        <label>
          Expected refund (CAD)
          <input
            type="number"
            step="0.01"
            value={form.refundExpectedCad}
            onChange={onFieldChange('refundExpectedCad')}
            placeholder="0.00"
          />
        </label>
        <label>
          Refund received (CAD)
          <input
            type="number"
            step="0.01"
            value={form.refundReceivedCad}
            onChange={onFieldChange('refundReceivedCad')}
            placeholder="0.00"
          />
        </label>
        <label>
          Refund deposit date
          <input type="date" value={form.refundReceivedAt} onChange={onFieldChange('refundReceivedAt')} />
        </label>
        <label>
          Deposit account
          <input
            type="text"
            value={form.refundDepositAccount}
            onChange={onFieldChange('refundDepositAccount')}
            placeholder="CIBC business op"
          />
        </label>
        <label>
          Deposit reference
          <input
            type="text"
            value={form.refundDepositReference}
            onChange={onFieldChange('refundDepositReference')}
            placeholder="e.g. CRA-BUS T2 REFUND"
          />
        </label>
        <label className="t2-assessment-grid-wide">
          CRA status (free text — shown on Overview card)
          <input
            type="text"
            value={form.craStatus}
            onChange={onFieldChange('craStatus')}
            placeholder="Auto-generated from milestones; override here."
          />
        </label>
        <label className="t2-assessment-grid-wide">
          Notes
          <textarea
            value={form.notes}
            onChange={onFieldChange('notes')}
            rows={2}
            placeholder="Any additional context (e.g. reassessment expected, instalment schedule)."
          />
        </label>
      </div>

      <div className="t2-assessment-actions">
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          onChange={onNoaFileChange}
        />
        <button
          type="button"
          className="t2-btn-secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading NOA…' : noaOnFile ? 'Replace NOA PDF' : 'Upload NOA PDF'}
        </button>
        {noaOnFile && period.artifacts.noa.viewUrl && (
          <a
            href={period.artifacts.noa.viewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="t2-btn-secondary t2-btn--link"
          >
            View NOA →
          </a>
        )}
        <button type="button" className="t2-btn-primary" onClick={persist} disabled={saving}>
          {saving ? 'Saving…' : 'Save assessment status'}
        </button>
        <button
          type="button"
          className="t2-btn-secondary t2-btn--link"
          onClick={() => setOpenLog((v) => !v)}
        >
          {openLog ? 'Hide' : 'Show'} CRA correspondence log ({correspondence.length})
        </button>
      </div>

      {openLog && (
        <div className="t2-assessment-log">
          <div className="t2-assessment-log-add">
            <select
              value={newEntry.correspondenceType}
              onChange={(e) => setNewEntry((p) => ({ ...p, correspondenceType: e.target.value }))}
            >
              <option value="noa">Notice of Assessment</option>
              <option value="reassessment">Reassessment</option>
              <option value="instalment_notice">Instalment notice</option>
              <option value="letter">Letter</option>
              <option value="phone_call">Phone call</option>
              <option value="email">Email</option>
              <option value="other">Other</option>
            </select>
            <input
              type="date"
              value={newEntry.occurredAt}
              onChange={(e) => setNewEntry((p) => ({ ...p, occurredAt: e.target.value }))}
            />
            <input
              type="text"
              placeholder="Subject"
              value={newEntry.subject}
              onChange={(e) => setNewEntry((p) => ({ ...p, subject: e.target.value }))}
            />
            <input
              type="text"
              placeholder="Notes / body"
              value={newEntry.body}
              onChange={(e) => setNewEntry((p) => ({ ...p, body: e.target.value }))}
            />
            <button type="button" className="t2-btn-secondary" onClick={addCorrespondence} disabled={addingEntry}>
              {addingEntry ? 'Adding…' : 'Log entry'}
            </button>
          </div>
          {correspondence.length === 0 ? (
            <p className="t2-helper">No CRA correspondence recorded yet.</p>
          ) : (
            <table className="t2-correspondence-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Subject</th>
                  <th>Notes</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {correspondence.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.occurred_at}</td>
                    <td>{entry.correspondence_type.replace(/_/g, ' ')}</td>
                    <td>{entry.subject || '—'}</td>
                    <td>{entry.body || '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="t2-btn-link"
                        onClick={() => deleteEntry(entry.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function T2OverviewTab({ summary, onGoToWorkflow, onReloadSummary }) {
  if (!summary) return <div className="t2-loading">Loading…</div>;
  const {
    entity, lastFiled, currentPeriod, nextFilingDeadline, daysToFilingDeadline,
    filedCount, assessedCount, pendingNoaCount, proactive, periodTimeline, documentReadiness,
  } = summary;

  const bannerCls = proactive?.level === 'critical' ? 't2-proactive-banner--critical'
    : proactive?.level === 'warning' ? 't2-proactive-banner--warning'
      : 't2-proactive-banner--ok';

  return (
    <div className="t2-overview">
      {proactive && (
        <div className={`t2-proactive-banner ${bannerCls}`}>
          <div className="t2-proactive-headline">{proactive.headline}</div>
          <p className="t2-proactive-body">{proactive.body}</p>
          {proactive.action === 'continue_workflow' && (
            <button type="button" className="t2-btn-primary" onClick={onGoToWorkflow}>Open workflow →</button>
          )}
          {proactive.action === 'view_filed' && lastFiled?.artifacts?.cor?.viewUrl && (
            <a
              href={lastFiled.artifacts.cor.viewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="t2-btn-secondary"
            >
              View submitted package →
            </a>
          )}
        </div>
      )}

      <div className="t2-cards-row">
        <div className="t2-card t2-card--entity">
          <div className="t2-card-header">
            <span className="t2-card-icon">🏛</span>
            <span className="t2-card-title">Corporation</span>
            <span className="t2-badge t2-badge--filed">RC0001</span>
          </div>
          <div className="t2-card-body">
            <div className="t2-kv"><span>Entity</span><span>{entity.legalName}</span></div>
            <div className="t2-kv"><span>BN</span><code>{entity.businessNumber}</code></div>
            <div className="t2-kv"><span>Account</span><code>{entity.rc0001}</code></div>
            <div className="t2-kv"><span>Returns on file</span><span>{assessedCount} assessed · {filedCount} filed · {pendingNoaCount} awaiting NOA</span></div>
          </div>
        </div>

        {lastFiled && (
          <div className="t2-card t2-card--filed">
            <div className="t2-card-header">
              <span className="t2-card-icon">✓</span>
              <span className="t2-card-title">Latest T2</span>
              <T2StatusBadge status={lastFiled.status} />
            </div>
            <div className="t2-card-body">
              <div className="t2-kv"><span>FY ending</span><span>{lastFiled.fye}</span></div>
              <div className="t2-kv"><span>Submitted</span><span>{lastFiled.filing?.submittedAt || '—'}</span></div>
              <div className="t2-kv"><span>CRA status</span><span>{lastFiled.filing?.craStatus || '—'}</span></div>
              {lastFiled.filing?.expectedRefund != null && (
                <div className="t2-kv"><span>Expected refund</span><strong>${lastFiled.filing.expectedRefund.toLocaleString()}</strong></div>
              )}
              {lastFiled.artifacts?.noa?.present && lastFiled.artifacts.noa.viewUrl && (
                <a href={lastFiled.artifacts.noa.viewUrl} target="_blank" rel="noopener noreferrer" className="t2-btn-secondary t2-btn--link">View NOA</a>
              )}
            </div>
          </div>
        )}

        {currentPeriod && (
          <div className={`t2-card t2-card--deadline${daysToFilingDeadline != null && daysToFilingDeadline <= 120 ? ' t2-card--urgent' : ''}`}>
            <div className="t2-card-header">
              <span className="t2-card-icon">📅</span>
              <span className="t2-card-title">Next filing</span>
              <T2StatusBadge status={currentPeriod.status} />
            </div>
            <div className="t2-card-body">
              <div className="t2-kv"><span>Period</span><span>{currentPeriod.periodFrom} → {currentPeriod.periodTo}</span></div>
              <div className="t2-kv"><span>FY ending</span><span>{currentPeriod.fye}</span></div>
              <div className="t2-kv"><span>Due</span><strong>{nextFilingDeadline}</strong></div>
              <div className="t2-kv"><span>Time left</span><T2DaysCountdown days={daysToFilingDeadline} /></div>
            </div>
          </div>
        )}
      </div>

      {lastFiled && (
        <T2AssessmentRefundCard period={lastFiled} onReload={onReloadSummary} />
      )}

      {periodTimeline?.length > 0 && (
        <div className="t2-section">
          <div className="t2-section-title">T2 filing status — all periods</div>
          <div className="t2-timeline">
            {periodTimeline.map((p) => (
              <div key={p.id} className={`t2-timeline-row t2-timeline-row--${p.status}`}>
                <div className="t2-timeline-main">
                  <span className="t2-timeline-label">{p.label}</span>
                  <span className="t2-timeline-meta">
                    FY {p.fye} · due {p.filingDue}
                    {p.status !== 'assessed' && p.daysToFilingDue != null && p.status !== 'submitted' ? ` · ${p.daysToFilingDue} days` : ''}
                  </span>
                  {p.submittedAt && <span className="t2-timeline-conf">Submitted {p.submittedAt}{p.expectedRefund != null ? ` · ~$${p.expectedRefund.toLocaleString()} refund expected` : ''}</span>}
                </div>
                <T2StatusBadge status={p.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {documentReadiness && (
        <div className="t2-section">
          <div className="t2-section-title">Document readiness (FY2026 package)</div>
          <p className="t2-helper">
            {documentReadiness.present} of {documentReadiness.total} documents present ({documentReadiness.compliancePercent}%).
            {' '}{documentReadiness.criticalPresent}/{documentReadiness.criticalTotal} critical items on file.
          </p>
          <button type="button" className="t2-btn-secondary" onClick={onGoToWorkflow}>Review workflow phases →</button>
        </div>
      )}
    </div>
  );
}

/** Build a view URL for a present T2 document file. */
function t2DocViewHref(file) {
  if (file.viewUrl) return file.viewUrl;
  if (file.linkedDocumentId) return `/opo/document/${file.linkedDocumentId}`;
  if (file.relPath) {
    return `/api/opo/auditor/compliance-document/view?file=${encodeURIComponent(file.relPath)}`;
  }
  return null;
}

function openT2DocView(file, e) {
  const href = t2DocViewHref(file);
  if (!href) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  e.preventDefault();
  if (file.linkedDocumentId) {
    window.open(href, '_blank');
  } else {
    window.open(href, '_blank', 'noopener,noreferrer');
  }
}
function PhasePriorityCount({ label, present, total, variant }) {
  if (!total) return null;
  const complete = present === total;
  const partial = present > 0 && present < total;
  const state = complete ? 'complete' : partial ? 'partial' : 'empty';
  return (
    <div className={`t2-priority-line t2-priority-line--${variant} t2-priority-line--${state}`}>
      <span className="t2-priority-line-label">{label}</span>
      <span className="t2-priority-line-value">{present} of {total}</span>
    </div>
  );
}

function phaseCompletionClass(docCounts) {
  const c = docCounts || {};
  const critDone = !c.critical || c.criticalPresent === c.critical;
  const reqDone = !c.required || c.requiredPresent === c.required;
  const recDone = !c.recommended || c.recommendedPresent === c.recommended;
  if (critDone && reqDone && recDone) return 'complete';
  if (critDone) return 'critical-done';
  return 'incomplete';
}

const T2AgentPanel = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Overview summary
  const [t2Summary, setT2Summary] = useState(null);

  // Workflow + requirements
  const [workflow, setWorkflow] = useState(null);
  const [workflowLoading, setWorkflowLoading] = useState(true);
  const [expandedPhase, setExpandedPhase] = useState(null);

  // Package builder
  const [packageRun, setPackageRun] = useState(null);
  const [building, setBuilding] = useState(false);
  const [packageError, setPackageError] = useState(null);

  // Send-to-auditor form
  const [auditorEmail, setAuditorEmail] = useState('');
  const [auditorName, setAuditorName] = useState('');
  const [sendStatus, setSendStatus] = useState(null);
  const [sending, setSending] = useState(false);

  // Chat
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatStreaming, setChatStreaming] = useState(false);
  const [chatStream, setChatStream] = useState('');
  const [chatInput, setChatInput] = useState('');
  const [activeModel, setActiveModel] = useState('');
  const chatEndRef = useRef(null);

  const reloadSummary = useCallback(() => {
    fetch('/api/opo/t2/summary')
      .then((r) => r.json())
      .then(setT2Summary)
      .catch((e) => console.error('[t2] summary:', e));
  }, []);

  // Initial load
  const reloadWorkflow = useCallback(() => {
    setWorkflowLoading(true);
    fetch('/api/opo/t2/workflow')
      .then((r) => r.json())
      .then((d) => setWorkflow(d))
      .catch((e) => console.error('[t2] workflow:', e))
      .finally(() => setWorkflowLoading(false));
  }, []);

  useEffect(() => {
    reloadSummary();
    reloadWorkflow();
    fetch('/api/opo/t2/models').then((r) => r.json()).then((d) => setActiveModel(d.active || '')).catch(() => {});
  }, [reloadSummary, reloadWorkflow]);

  const handleGoToWorkflow = useCallback(() => {
    setActiveTab('workflow');
  }, []);

  // Chat history load
  useEffect(() => {
    if (activeTab !== 'chat') return;
    setChatLoading(true);
    fetch('/api/opo/t2/chat/history')
      .then((r) => r.json())
      .then((d) => setChatHistory(d.history || []))
      .catch(() => {})
      .finally(() => setChatLoading(false));
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'chat') return;
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, chatStream, activeTab]);

  const buildPackage = () => {
    setBuilding(true);
    setPackageError(null);
    fetch('/api/opo/t2/package', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
      .then((r) => r.ok ? r.json() : r.json().then((e) => Promise.reject(new Error(e.error || 'Build failed'))))
      .then((d) => { setPackageRun(d); setActiveTab('package'); })
      .catch((e) => setPackageError(e.message))
      .finally(() => setBuilding(false));
  };

  const sendPackage = () => {
    if (!packageRun) return;
    if (!auditorEmail.includes('@')) { setSendStatus({ error: 'Please enter a valid email address.' }); return; }
    setSending(true);
    setSendStatus(null);
    fetch(`/api/opo/t2/package/${packageRun.id}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ auditorEmail, auditorName }),
    })
      .then((r) => r.ok ? r.json() : r.json().then((e) => Promise.reject(new Error(e.error || 'Send failed'))))
      .then((d) => setSendStatus({ ok: true, ...d }))
      .catch((e) => setSendStatus({ error: e.message }))
      .finally(() => setSending(false));
  };

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatStreaming) return;
    setChatInput('');
    setChatStreaming(true);
    setChatStream('');
    setChatHistory((h) => [...h, { role: 'user', content: msg, created_at: new Date().toISOString() }]);
    try {
      const res = await fetch('/api/opo/t2/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        setChatStream(full);
      }
      setChatHistory((h) => [...h, { role: 'assistant', content: full, created_at: new Date().toISOString() }]);
      setChatStream('');
    } catch (e) {
      setChatStream('Error: ' + (e.message || 'request failed'));
    } finally {
      setChatStreaming(false);
    }
  };

  const onChatKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); }
  };

  const docSummary = workflow?.requirements?.summary;
  const allCriticalReady = useMemo(() => {
    if (!workflow) return false;
    return workflow.phases.every((p) => p.docCounts.critical === 0 || p.docCounts.criticalPresent === p.docCounts.critical);
  }, [workflow]);

  const alertIcon = t2Summary?.proactive?.level === 'critical' ? '🚨'
    : t2Summary?.proactive?.level === 'warning' ? '⚠️' : '✓';

  return (
    <div className="t2-panel">
      <div className="t2-panel-header">
        <div>
          <h2 className="t2-panel-title">OptiPrime T2</h2>
          <p className="t2-panel-subtitle">
            T2 corporate income tax — {t2Summary?.entity?.legalName || 'OptiPrime AI Inc.'} · RC0001 · FYE April 30.
          </p>
        </div>
        {docSummary && (
          <div className="t2-summary-pill" title={`${docSummary.present}/${docSummary.total} documents present`}>
            <div className="t2-summary-pct">{docSummary.compliancePercent}%</div>
            <div className="t2-summary-lbl">Documents ready</div>
            <div className="t2-summary-crit">
              {docSummary.criticalPresent}/{docSummary.criticalTotal} critical
            </div>
          </div>
        )}
      </div>

      {t2Summary?.proactive && (
        <div className={`t2-panel-alert t2-panel-alert--${t2Summary.proactive.level}`}>
          <span>{alertIcon}</span>
          <span>
            {t2Summary.proactive.headline}
            {t2Summary.daysToFilingDeadline != null && t2Summary.proactive.action === 'continue_workflow' && (
              <> — due {t2Summary.nextFilingDeadline} · <T2DaysCountdown days={t2Summary.daysToFilingDeadline} /></>
            )}
          </span>
        </div>
      )}

      <div className="t2-tabs" role="tablist">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'workflow', label: 'Workflow' },
          { id: 'package', label: 'Package & handoff' },
          { id: 'chat', label: 'Conversation' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            className={`t2-tab${activeTab === t.id ? ' is-active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
        {activeTab === 'chat' && activeModel && (
          <span className="t2-tab-model" title="Active Ollama model (cascade picks the largest available)">
            {activeModel}
          </span>
        )}
      </div>

      {activeTab === 'overview' && (
        <div className="t2-tab-body">
          <T2OverviewTab summary={t2Summary} onGoToWorkflow={handleGoToWorkflow} onReloadSummary={reloadSummary} />
        </div>
      )}

      {activeTab === 'workflow' && (
        <div className="t2-tab-body">
          {workflowLoading && <div className="t2-loading">Loading workflow…</div>}
          {!workflowLoading && workflow && (
            <ol className="t2-phases">
              {workflow.phases.map((p, idx) => {
                const open = expandedPhase === p.id;
                const dc = p.docCounts || {};
                const completion = phaseCompletionClass(dc);
                return (
                  <li key={p.id} className={`t2-phase t2-phase--${completion}`}>
                    <div className="t2-phase-head" onClick={() => setExpandedPhase(open ? null : p.id)}>
                      <div className="t2-phase-head-left">
                        <span className="t2-phase-step">{idx + 1}</span>
                        <div>
                          <div className="t2-phase-title">{p.title}</div>
                          <div className="t2-phase-id">{p.id}</div>
                        </div>
                      </div>
                      <div className="t2-phase-head-right">
                        <div className="t2-priority-counts">
                          <PhasePriorityCount label="Critical" present={dc.criticalPresent} total={dc.critical} variant="critical" />
                          <PhasePriorityCount label="Required" present={dc.requiredPresent} total={dc.required} variant="required" />
                          <PhasePriorityCount label="Recommended" present={dc.recommendedPresent} total={dc.recommended} variant="recommended" />
                        </div>
                      </div>
                    </div>
                    {open && (
                      <div className="t2-phase-body">
                        {p.objective && <p className="t2-phase-objective">{p.objective}</p>}
                        {p.steps?.length > 0 && (
                          <div className="t2-phase-section">
                            <div className="t2-phase-section-label">Steps</div>
                            <ol className="t2-phase-steps">
                              {p.steps.map((s, i) => <li key={i}>{s}</li>)}
                            </ol>
                          </div>
                        )}
                        {p.gate && (
                          <div className="t2-phase-section">
                            <div className="t2-phase-section-label">Gate</div>
                            <div className="t2-phase-gate">{p.gate}</div>
                          </div>
                        )}
                        {p.docs?.length > 0 && (
                          <div className="t2-phase-section">
                            <div className="t2-phase-section-label">Documents</div>
                            <table className="t2-phase-docs">
                              <thead>
                                <tr>
                                  <th>Document</th>
                                  <th>Priority</th>
                                  <th>Path</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.docs.map((d) => (
                                  <tr key={d.itemKey} className={`t2-doc-row t2-doc-row--${d.status}`}>
                                    <td>
                                      {d.status === 'present' && d.files?.length > 0 ? (
                                        d.files.length === 1 ? (
                                          <a
                                            href={t2DocViewHref(d.files[0])}
                                            className="t2-doc-link"
                                            title={`View ${d.files[0].relPath}`}
                                            onClick={(e) => openT2DocView(d.files[0], e)}
                                          >
                                            {d.document}
                                          </a>
                                        ) : (
                                          <span className="t2-doc-multi">
                                            {d.document}
                                            <span className="t2-doc-file-links">
                                              {d.files.map((f) => (
                                                <a
                                                  key={f.relPath}
                                                  href={t2DocViewHref(f)}
                                                  className="t2-doc-link t2-doc-link--file"
                                                  title={f.relPath}
                                                  onClick={(e) => openT2DocView(f, e)}
                                                >
                                                  {f.relPath.split('/').pop()}
                                                </a>
                                              ))}
                                            </span>
                                          </span>
                                        )
                                      ) : (
                                        d.document
                                      )}
                                    </td>
                                    <td><span className={`t2-pri t2-pri--${d.priority}`}>{d.priority}</span></td>
                                    <td className="t2-doc-path"><code>{d.expectedPath}</code></td>
                                    <td>
                                      {d.status === 'present' ? (
                                        <span className="t2-doc-present">
                                          ✓ {d.files.length} file{d.files.length !== 1 ? 's' : ''}
                                          {d.files.length === 1 && (
                                            <>
                                              {' · '}
                                              <a
                                                href={t2DocViewHref(d.files[0])}
                                                className="t2-doc-link t2-doc-link--inline"
                                                onClick={(e) => openT2DocView(d.files[0], e)}
                                              >
                                                View
                                              </a>
                                            </>
                                          )}
                                        </span>
                                      ) : (
                                        <span className="t2-doc-missing">missing</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {p.notes ? (
                          <div className="t2-phase-notes">
                            <strong>Notes:</strong> {p.notes}
                          </div>
                        ) : null}
                        {p.auditorNotes && (
                          <div className="t2-phase-auditor-note">
                            <strong>Auditor note:</strong> {p.auditorNotes}
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {activeTab === 'package' && (
        <div className="t2-tab-body">
          <div className="t2-package-section">
            <h3>Build T2 package</h3>
            <p className="t2-helper">
              Assembles every present document into a single ZIP organized by section, with an auto-generated
              cover letter and PDF manifest (SHA-256 verified). You can build the package at any time — it just
              snapshots whatever is currently in <code>01_auditor/</code>.
            </p>
            {!allCriticalReady && (
              <div className="t2-warning">
                ⚠ Not all critical documents are present yet ({docSummary?.criticalPresent}/{docSummary?.criticalTotal}).
                You can still build a package for review, but the auditor will likely flag missing items.
              </div>
            )}
            <button type="button" className="t2-btn-primary" onClick={buildPackage} disabled={building}>
              {building ? 'Building package…' : 'Build T2 package'}
            </button>
            {packageError && <div className="t2-error">{packageError}</div>}
          </div>

          {packageRun && (
            <div className="t2-package-section">
              <h3>Latest package</h3>
              <table className="t2-package-meta">
                <tbody>
                  <tr><th>Package</th><td>{packageRun.packageBasename}</td></tr>
                  <tr><th>Files</th><td>{packageRun.fileCount}</td></tr>
                  <tr><th>Size</th><td>{(packageRun.totalBytes / (1024 * 1024)).toFixed(2)} MB</td></tr>
                  <tr><th>SHA-256</th><td><code className="t2-sha">{packageRun.sha256}</code></td></tr>
                  <tr><th>Manifest readiness</th><td>{packageRun.summary?.present}/{packageRun.summary?.total} ({packageRun.summary?.compliancePercent}%)</td></tr>
                </tbody>
              </table>
              <div className="t2-package-actions">
                <a className="t2-btn-secondary" href={packageRun.downloadUrl}>Download ZIP</a>
                <a className="t2-btn-secondary" href={packageRun.manifestUrl} target="_blank" rel="noreferrer">View manifest PDF</a>
              </div>
            </div>
          )}

          {packageRun && (
            <div className="t2-package-section">
              <h3>Email package to auditor</h3>
              <p className="t2-helper">
                Sends the ZIP, cover letter, and manifest PDF to a licensed Canadian auditor along with a single-use
                review link valid for 30 days. The auditor uses the same OptiPrime interface (token-gated) to review
                and submit the T2.
              </p>
              <div className="t2-send-form">
                <label>
                  Auditor email
                  <input
                    type="email"
                    value={auditorEmail}
                    onChange={(e) => setAuditorEmail(e.target.value)}
                    placeholder="auditor@example.com"
                  />
                </label>
                <label>
                  Auditor name (optional)
                  <input
                    type="text"
                    value={auditorName}
                    onChange={(e) => setAuditorName(e.target.value)}
                    placeholder="Jane Smith, CPA, CA"
                  />
                </label>
                <button type="button" className="t2-btn-primary" onClick={sendPackage} disabled={sending}>
                  {sending ? 'Sending…' : 'Send to auditor'}
                </button>
                {sendStatus?.error && <div className="t2-error">{sendStatus.error}</div>}
                {sendStatus?.ok && (
                  <div className="t2-success">
                    Sent. Review link expires {new Date(sendStatus.expiresAt).toLocaleDateString()}. <br/>
                    Auditor URL: <code>{sendStatus.reviewUrl}</code>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'chat' && (
        <div className="t2-tab-body t2-chat-body">
          <div className="t2-chat-messages">
            {chatLoading && <div className="t2-chat-msg assistant">Loading history…</div>}
            {!chatLoading && chatHistory.length === 0 && !chatStream && (
              <div className="t2-chat-empty">
                <p><strong>Ask OptiPrime T2 anything about the corporate return.</strong></p>
                <p>The agent has full context of your workflow status, bank-truth summary, and document readiness.</p>
                <ul>
                  <li>"What's blocking us on Phase 4?"</li>
                  <li>"Walk me through Schedule 1 line by line for our numbers."</li>
                  <li>"Should we elect ITA s.37 for the DGX Spark or use Class 50 CCA?"</li>
                  <li>"What changes when we move from pre-revenue to revenue next year?"</li>
                </ul>
              </div>
            )}
            {chatHistory.map((m, i) => (
              <div key={i} className={`t2-chat-msg ${m.role}`}>
                <div className="t2-chat-msg-body">{m.content}</div>
                {m.created_at && <div className="t2-chat-msg-meta">{new Date(m.created_at).toLocaleString()}</div>}
              </div>
            ))}
            {chatStream && (
              <div className="t2-chat-msg assistant">
                <div className="t2-chat-msg-body">{chatStream}<span className="t2-chat-cursor">▌</span></div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div className="t2-chat-input">
            <textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={onChatKey}
              placeholder="Ask OptiPrime T2… (Enter to send, Shift+Enter for new line)"
              disabled={chatStreaming}
              rows={2}
            />
            <button type="button" className="t2-btn-primary" onClick={sendChat} disabled={chatStreaming}>
              {chatStreaming ? 'Thinking…' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default T2AgentPanel;
